import React, { useEffect, useState } from "react";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billede1.jpg";
import "./AdminMessagePage.css";
import Modal from "../../../components/Modal/Modal";
import { getAuthHeaders } from "../../../api/authHelper";
interface Event {
    eventID: number;
    eventName: string;
}

interface FunctionItem {
    functionId: number;
    functionName: string;
    functionCategoryId?: number;
}

interface MessageLogItem {
    messageLogId: number;
    subject: string;
    body: string;
    eventName: string;
    functionName?: string | null;
    sentByName: string;
    sentAt: string;
    recipientCount: number;
    deliveredCount: number;
    failedCount: number;
}

const AdminMessagePage: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL as string;
    const currentUserId = Number(sessionStorage.getItem("userId") || 0);

    const [activeTab, setActiveTab] = useState<"send" | "logs">("send");

    const [events, setEvents] = useState<Event[]>([]);
    const [functions, setFunctions] = useState<FunctionItem[]>([]);

    const [selectedEvent, setSelectedEvent] = useState<number | "">("");
    const [selectedFunction, setSelectedFunction] = useState<number | "all" | "">("");

    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");

    const [recipientCount, setRecipientCount] = useState<number | null>(null);

    const [logs, setLogs] = useState<MessageLogItem[]>([]);
    const [logsLoading, setLogsLoading] = useState(false);

    const [status, setStatus] = useState<{ type: "ok" | "err"; text: string } | null>(null);

    const [showConfirm, setShowConfirm] = useState(false);
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        fetch(`${apiUrl}/api/events`, {
            headers: getAuthHeaders(),
        })
            .then((res) => res.json())
            .then((data) => setEvents(data))
            .catch((err) => console.error("Failed to load events", err));
    }, [apiUrl]);

    useEffect(() => {
        if (!selectedEvent) {
            setFunctions([]);
            setSelectedFunction("");
            return;
        }

        fetch(`${apiUrl}/api/events/${selectedEvent}/functions`, {
            headers: getAuthHeaders(),
        })
            .then((res) => res.json())
            .then((data) => {
                const filtered = Array.isArray(data)
                    ? data.filter((x: FunctionItem) => x.functionCategoryId === 10)
                    : [];
                setFunctions(filtered);
            })
            .catch((err) => console.error("Failed to load functions", err));
    }, [selectedEvent, apiUrl]);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!isSending) return;

            e.preventDefault();
            e.returnValue = "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isSending]);





    useEffect(() => {
        if (!selectedEvent || selectedFunction === "") {
            setRecipientCount(null);
            return;
        }

        let cancelled = false;

        const loadRecipientCount = async () => {
            try {
                setRecipientCount(null);

                const query =
                    selectedFunction === "all"
                        ? `eventId=${selectedEvent}&functionCategoryId=10`
                        : `eventId=${selectedEvent}&functionId=${selectedFunction}`;

                const res = await fetch(
                    `${apiUrl}/api/messages/recipients/count?${query}`,
                    {
                        headers: getAuthHeaders(),
                    }
                );

                if (!res.ok) {
                    if (!cancelled) setRecipientCount(0);
                    return;
                }

                const data = await res.json();

                if (!cancelled) {
                    setRecipientCount(typeof data.count === "number" ? data.count : 0);
                }
            } catch (err) {
                console.error("Failed to load recipient count", err);
                if (!cancelled) setRecipientCount(0);
            }
        };

        loadRecipientCount();

        return () => {
            cancelled = true;
        };
    }, [apiUrl, selectedEvent, selectedFunction]);

    const loadLogs = async () => {
        try {
            setLogsLoading(true);
            const res = await fetch(`${apiUrl}/api/messages/logs`, {
                headers: getAuthHeaders(),
            })
            const data = await res.json();
            setLogs(data);
        } catch (err) {
            console.error("Failed to load logs", err);
        } finally {
            setLogsLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === "logs") {
            loadLogs();
        }
    }, [activeTab]);

    useEffect(() => {
        if (!status || status.type !== "ok") return;
        const t = setTimeout(() => setStatus(null), 5000);
        return () => clearTimeout(t);
    }, [status]);

    const validate = () => {
        if (!selectedEvent || selectedFunction === "" || !subject.trim() || !body.trim()) {
            setStatus({ type: "err", text: "Udfyld event, funktion, emne og besked før du sender." });
            return false;
        }

        if (currentUserId <= 0) {
            setStatus({ type: "err", text: "Kunne ikke finde bruger-ID." });
            return false;
        }

        if (recipientCount === 0) {
            setStatus({ type: "err", text: "Der er ingen modtagere på den valgte kombination." });
            return false;
        }

        if ((recipientCount ?? 0) > 2000) {
            setStatus({
                type: "err",
                text: "Du kan højst sende til 2000 modtagere ad gangen.",
            });
            return false;
        }

        return true;
    };

    const openConfirm = () => {
        setStatus(null);
        if (!validate()) return;
        setShowConfirm(true);
    };

    const sendMessage = async () => {
        if (!validate()) return;

        setIsSending(true);

        const payload = {
            eventId: selectedEvent,
            functionId: selectedFunction === "all" ? null : selectedFunction,
            functionCategoryId: selectedFunction === "all" ? 10 : null,
            subject,
            body,
            sentByUserId: currentUserId,
        };

        try {
            const res = await fetch(`${apiUrl}/api/messages/send`, {
                method: "POST",
                headers: getAuthHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify(payload),
            });

            if (res.ok) {
                const data = await res.json();

                const totalRecipients = data.recipientCount ?? recipientCount ?? 0;

                setStatus({
                    type: "ok",
                    text:
                        totalRecipients > 300
                            ? `Beskeden er sendt færdig i batches. Modtagere: ${totalRecipients}.`
                            : `Beskeden er sendt. Modtagere: ${totalRecipients}.`,
                });

                setSubject("");
                setBody("");
                setShowConfirm(false);
            } else {
                let errMsg = "Ukendt fejl";

                try {
                    const err = await res.json();
                    errMsg = err.message || JSON.stringify(err);
                } catch {
                    const txt = await res.text();
                    if (txt) errMsg = txt;
                }

                setStatus({ type: "err", text: `Fejl ved afsendelse: ${errMsg}` });
            }
        } catch (err) {
            setStatus({ type: "err", text: `Netværksfejl: ${(err as Error).message}` });
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Group Messages"
                titleDa="Gruppebeskeder"
                descriptionEn="Send messages to volunteers"
                descriptionDa="Send beskeder til frivillige"
                imageSrc={Billede1}
            />

            <div className="user-admin-page">
                <div className="admin-msg-card">
                    <div className="admin-msg-header">
                        <h2 className="admin-msg-title">Gruppebeskeder</h2>

                        <div className="admin-msg-tabs">
                            <button
                                className={`admin-msg-tab ${activeTab === "send" ? "active" : ""}`}
                                onClick={() => setActiveTab("send")}
                                type="button"
                            >
                                Send besked
                            </button>

                            <button
                                className={`admin-msg-tab ${activeTab === "logs" ? "active" : ""}`}
                                onClick={() => setActiveTab("logs")}
                                type="button"
                            >
                                Log
                            </button>
                        </div>
                    </div>

                    {activeTab === "send" && (
                        <div className="admin-send-layout">
                            <div className="admin-send-main">
                                <div className="admin-msg-row">
                                    <div className="admin-msg-field">
                                        <label className="admin-msg-label">Event</label>
                                        <select
                                            className="admin-msg-input"
                                            value={selectedEvent}
                                            onChange={(e) => setSelectedEvent(Number(e.target.value) || "")}
                                            disabled={isSending}
                                        >
                                            <option value="">Vælg event</option>
                                            {events.map((event) => (
                                                <option key={event.eventID} value={event.eventID}>
                                                    {event.eventName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="admin-msg-field">
                                        <label className="admin-msg-label">Funktion</label>
                                        <select
                                            className="admin-msg-input"
                                            value={selectedFunction}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setSelectedFunction(value === "all" ? "all" : Number(value) || "");
                                            }}
                                            disabled={!selectedEvent || isSending}
                                        >
                                            <option value="">Vælg funktion</option>
                                            <option value="all">Alle funktioner</option>

                                            {functions.map((fn) => (
                                                <option key={fn.functionId} value={fn.functionId}>
                                                    {fn.functionName?.trim()}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="admin-msg-field">
                                    <label className="admin-msg-label">Emne</label>
                                    <input
                                        className="admin-msg-input"
                                        type="text"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        placeholder="Skriv emne her..."
                                        disabled={isSending}
                                    />
                                </div>

                                <div className="admin-msg-field">
                                    <label className="admin-msg-label">Besked</label>
                                    <textarea
                                        className="admin-msg-textarea"
                                        rows={10}
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        placeholder={`Skriv besked her...

Du kan godt lave linjeskift.
De kommer med i mailen, hvis backend erstatter linjeskift med <br/>.`}
                                        disabled={isSending}
                                    />
                                </div>

                                <div className="admin-send-actions">
                                    <button
                                        className="btn-send"
                                        onClick={openConfirm}
                                        disabled={isSending}
                                        type="button"
                                    >
                                        {isSending ? "Sender..." : "Send besked"}
                                    </button>
                                </div>
                                {recipientCount !== null && recipientCount > 500 && (
                                    <div className="admin-msg-warning">
                                        ⚠️ Denne besked sendes til {recipientCount} modtagere.
                                        <br />
                                        Systemet sender maks 500 mails pr. minut, så det kan tage lidt tid.
                                        <br />
                                        Luk ikke siden eller fanen før afsendelsen er færdig.
                                    </div>
                                )}

                                {status && (
                                    <div className={`admin-msg-status ${status.type === "ok" ? "is-ok" : "is-err"}`}>
                                        {status.text}
                                    </div>
                                )}
                            </div>

                            <aside className="admin-send-side">
                                <div className="send-side-card">
                                    <div className="send-side-title">Overblik</div>

                                    <div className="send-side-item">
                                        <span>Event</span>
                                        <strong>
                                            {events.find((e) => e.eventID === selectedEvent)?.eventName || "-"}
                                        </strong>
                                    </div>

                                    <div className="send-side-item">
                                        <span>Funktion</span>
                                        <strong>
                                            {selectedFunction === "all"
                                                ? "Alle funktioner"
                                                : functions.find((f) => f.functionId === selectedFunction)?.functionName || "-"}
                                        </strong>
                                    </div>

                                    <div className="send-side-item">
                                        <span>Modtagere</span>
                                        <strong>
                                            {recipientCount === null ? "Henter..." : recipientCount}
                                        </strong>
                                    </div>

                                    <div className="send-side-item">
                                        <span>Emne længde</span>
                                        <strong>{subject.length}</strong>
                                    </div>
                                </div>

                                <div className="send-side-card accent">
                                    <div className="send-side-title">Tip</div>
                                    <p className="send-tip-text">
                                        Hold emnet kort og tydeligt. Brug gerne linjeskift i beskeden, så mailen er nemmere at læse.
                                    </p>
                                </div>
                            </aside>
                        </div>
                    )}

                    {activeTab === "logs" && (
                        <>
                            <div className="admin-msg-log-top">
                                <div className="admin-msg-log-summary">
                                    <div className="log-stat-card">
                                        <span className="log-stat-label">Beskeder</span>
                                        <strong className="log-stat-value">{logs.length}</strong>
                                    </div>

                                    <div className="log-stat-card">
                                        <span className="log-stat-label">Samlet leveret</span>
                                        <strong className="log-stat-value">
                                            {logs.reduce((s, x) => s + x.deliveredCount, 0)}
                                        </strong>
                                    </div>

                                    <div className="log-stat-card">
                                        <span className="log-stat-label">Samlet fejlet</span>
                                        <strong className="log-stat-value">
                                            {logs.reduce((s, x) => s + x.failedCount, 0)}
                                        </strong>
                                    </div>
                                </div>

                                <button className="btn-refresh" onClick={loadLogs} type="button">
                                    Opdater log
                                </button>
                            </div>

                            {logsLoading ? (
                                <div className="admin-msg-empty">Henter log...</div>
                            ) : logs.length === 0 ? (
                                <div className="admin-msg-empty">Ingen beskeder fundet.</div>
                            ) : (
                                <div className="log-table-card">
                                    <div className="table-wrap">
                                        <table className="admin-log-table">
                                            <thead>
                                                <tr>
                                                    <th>Emne</th>
                                                    <th>Event</th>
                                                    <th>Funktion</th>
                                                    <th>Sendt af</th>
                                                    <th>Dato</th>
                                                    <th>Modtagere</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {logs.map((log) => (
                                                    <tr key={log.messageLogId}>
                                                        <td>
                                                            <div className="log-subject">
                                                                <strong>{log.subject}</strong>
                                                                <div className="log-preview">
                                                                    {log.body?.length > 80
                                                                        ? `${log.body.slice(0, 80)}...`
                                                                        : log.body}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>{log.eventName || "-"}</td>
                                                        <td>{log.functionName || "-"}</td>
                                                        <td>{log.sentByName || "-"}</td>
                                                        <td>{new Date(log.sentAt).toLocaleString("da-DK")}</td>
                                                        <td>{log.recipientCount}</td>
                                                        <td>
                                                            <div className="log-status">
                                                                <span className="success">Leveret: {log.deliveredCount}</span>
                                                                <span className="error">Fejlet: {log.failedCount}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <Modal
                isOpen={showConfirm}
                title="Bekræft afsendelse"
                onClose={() => !isSending && setShowConfirm(false)}
            >
                <div className="confirm-wrap">
                    <p className="confirm-text">
                        Er du sikker på, at du vil sende denne besked?
                    </p>

                    <div className="confirm-meta">
                        <div>
                            <strong>Event:</strong>{" "}
                            {events.find((e) => e.eventID === selectedEvent)?.eventName || "-"}
                        </div>
                        <div>
                            <strong>Funktion:</strong>{" "}
                            {selectedFunction === "all"
                                ? "Alle funktioner"
                                : functions.find((f) => f.functionId === selectedFunction)?.functionName || "-"}
                        </div>
                        <div>
                            <strong>Modtagere:</strong>{" "}
                            {recipientCount === null ? "Henter..." : `${recipientCount} personer`}
                        </div>
                        <div>
                            <strong>Emne:</strong> {subject || "-"}
                        </div>
                    </div>

                    <div className="confirm-actions">
                        <button
                            className="btn-secondary"
                            onClick={() => setShowConfirm(false)}
                            disabled={isSending}
                            type="button"
                        >
                            Annuller
                        </button>

                        <button
                            className="btn-send"
                            onClick={sendMessage}
                            disabled={isSending}
                            type="button"
                        >
                            {isSending
                                ? recipientCount !== null && recipientCount > 500
                                    ? "Sender i batches..."
                                    : "Sender..."
                                : "Ja, send"}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AdminMessagePage;